import { yupResolver } from '@hookform/resolvers/yup';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../types/api';
import { queryString } from '../../../../../utils/helpers/http';
import { getChangedValues } from '../../../../../utils/helpers/object';
import FieldWithErrors from '../../../../Forms/ReactHookForm/FieldWithErrors';
import { LogisticsRoute } from '../../../LogisticsRoutes/LogisticsRoutes.functions';
import {
  TransportEntitiesType,
  TransportEntitiesTypes,
} from '../../TransportEntities.functions';
import {
  getDefaultValues,
  getValidationSchema,
} from './CreateDialogTransportEntities.functions';

type Props = {
  isEditDialog: boolean;
  transportEntityData: TransportEntitiesType | undefined;
  handleCreation: (data: any) => void;
  handleEditing: (data: any) => void;
};

function FormContainer({
  isEditDialog,
  transportEntityData,
  handleCreation,
  handleEditing,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const defaultValues = useMemo(
    () => getDefaultValues(isEditDialog, transportEntityData),
    [isEditDialog, transportEntityData]
  );

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const { handleSubmit, reset } = methods;

  const { data: transportEntityTypes } = useAxiosHook<
    WithData<TransportEntitiesTypes[]>
  >(
    '/v2/transport-entity-types' +
      queryString({
        page: 1,
        limit: 0,
      })
  );

  const transportEntityTypesOptions = useMemo(
    () =>
      transportEntityTypes?.data.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [transportEntityTypes]
  );

  const { data: logisticPointsTypes } = useAxiosHook<
    WithData<LogisticsRoute[]>
  >(
    '/logistics-point' +
      queryString({
        pointTypeId: 1,
        page: 1,
        limit: 10000,
      })
  );

  const logisticPointsTypesOptions = useMemo<any>(
    () =>
      logisticPointsTypes?.data.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [logisticPointsTypes]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  function handleFormSubmission(values: any) {
    if (isEditDialog) {
      handleEditing(getChangedValues(values, defaultValues));
      return;
    }

    handleCreation(values);
  }

  return (
    <FormProvider {...methods}>
      <form
        id="transport-entity-create-edit-form"
        onSubmit={handleSubmit(handleFormSubmission)}
      >
        <div className="p-fluid">
          <FieldWithErrors name="barcode" label={t('Barcode')}>
            <Controller
              name="barcode"
              render={({ field }) => (
                <InputText
                  id="barcode"
                  name="barcode"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="transportEntityType.id"
            label={t('Transport entity type')}
          >
            <Controller
              name="transportEntityType.id"
              render={({ field }) => (
                <Dropdown
                  placeholder={t('Search')}
                  options={transportEntityTypesOptions}
                  value={field.value}
                  onChange={field.onChange}
                  className="data-cy-transportEntityType-options"
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="ownerLogisticsPoint.id"
            label={t('Owner logistic point')}
          >
            <Controller
              name="ownerLogisticsPoint.id"
              render={({ field }) => (
                <Dropdown
                  placeholder={t('Search')}
                  options={logisticPointsTypesOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="currentLogisticsPoint.id"
            label={t('Current logistic point')}
          >
            <Controller
              name="currentLogisticsPoint.id"
              render={({ field }) => (
                <Dropdown
                  placeholder={t('Search')}
                  options={logisticPointsTypesOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          {/* <FieldWithErrors
            name="destinationLogisticsPoint.id"
            label={t('Destination logistic point')}
          >
            <Controller
              name="destinationLogisticsPoint.id"
              render={({ field }) => (
                <Dropdown
                  placeholder={t('Search')}
                  options={logisticPointsTypesOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors> */}

          <FieldWithErrors name="volume" label={t('Volume')}>
            <Controller
              name="volume"
              render={({ field }) => (
                <InputText
                  id="volume"
                  name="volume"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>
        </div>
      </form>
    </FormProvider>
  );
}

export default FormContainer;
